"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CONNECT_RETURN_PARAMS = exports.CONNECT_OUTCOMES = exports.trackerConnectionSchema = exports.oauthStartRequestSchema = exports.unroutedTaskSchema = exports.taskIssueLinkSchema = exports.discardParkedRequestSchema = exports.parkedSummarySchema = exports.parkedWorklogSchema = exports.PARKED_REASONS = exports.setIntegrationActiveRequestSchema = exports.saveFieldMapsRequestSchema = exports.fieldStepSchema = exports.linkTaskRequestSchema = exports.pairIdentityRequestSchema = exports.identityRowSchema = exports.instanceTargetsSchema = exports.instanceTargetSchema = exports.stagedProjectSchema = exports.discoverResultSchema = exports.probeResultSchema = exports.probeRequestSchema = exports.saveIntegrationRequestSchema = exports.integrationRunDetailSchema = exports.integrationRunRecordSchema = exports.syncRunResponseSchema = exports.syncRunRequestSchema = exports.integrationRunSchema = exports.webhookManualSetupSchema = exports.remoteIssueTypeSchema = exports.remotePriorityChoiceSchema = exports.remoteStatusChoiceSchema = exports.integrationInstanceSchema = exports.userIdentitySchema = exports.priorityMapEntrySchema = exports.statusMapEntrySchema = exports.PROVIDER_HINTS = exports.WIZARD_STEPS = exports.RUN_RESULTS = exports.RUN_DIRECTIONS = exports.RUN_STATES = exports.RUN_TRIGGERS = exports.OVERWATCH_HOURS = exports.overwatchAtSchema = exports.CONFLICT_WINNERS = exports.INTEGRATION_AUTH_KINDS = exports.INTEGRATION_STATES = exports.syncRulesSchema = exports.SYNC_RULES = void 0;
exports.providerHints = providerHints;
exports.groupDefaultProblems = groupDefaultProblems;
exports.mappedCount = mappedCount;
exports.activeRules = activeRules;
exports.remoteIssueUrl = remoteIssueUrl;
const zod_1 = require("zod");
const catalogue_1 = require("../extensions/catalogue");
const identifier_1 = require("../identifier");
const pagination_1 = require("../pagination");
const metafields_1 = require("../metafields/metafields");
const remote_1 = require("./remote");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C33 · Integration instances. `2fazis.main.jsx` 1407–1560, 2116–2250.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * An INSTANCE is one connection to one remote system. `0059` recorded the workspace saying *"we use
 * Jira"*; this is *which* Jira — and there can be more than one, which is the mockup's own insistence
 * (*"külön a saját és külön az ügyfél rendszere"*).
 *
 * ⚠️⚠️ **NOTHING IN THIS CHUNK TALKS TO ANYTHING.** `chunks.md` C33: *"No network — built against a
 * stub."* Every run in the log is produced locally so the counters, the run detail and the error
 * list have something true-shaped to draw. C34 replaces the stub and changes none of these shapes.
 *
 * ⚠️⚠️ **NO SECRET IS EVER ON THE WIRE OUTBOUND.** `integrationInstanceSchema` carries `authHint` —
 * the last four characters — and nothing else. A token travels once, inbound, in a create or update
 * request; the API encrypts it and it never comes back. `0060` makes the same argument at the column.
 */
/* ⚠️ Its own copy, as `contracts/contract.ts` keeps one: a wire date is `YYYY-MM-DD` and nothing
   else, and `dates.ts` exports the helpers rather than the schema. */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
/**
 * The five switches, in the mockup's order.
 *
 * ⚠️⚠️ **THE MOCKUP'S `INTEG_RULES` HAD SEVEN AND TWO ARE GONE.** P2-22 put comments and
 * attachments out of scope rather than deferring them, so `0063` dropped both columns. Until it
 * did, the wizard drew two switches for features that will never be built — which is the same shape
 * as a marketplace entry promising a provider nobody wrote.
 */
/**
 * ⚠️⚠️ **THREE MORE ARE GONE, AND THEY WENT FOR A WORSE REASON THAN THE FIRST TWO.** Comments
 * and attachments were dropped above because they were never going to be built — switches for a
 * feature that did not exist. `taskIn`, `worklogIn` and `worklogOut` were switches for features
 * that DO exist and that they did not control: they were written, returned to the client and drawn
 * as toggles, and **no service ever read them**. `rule_task_out` and `rule_status_both` are checked
 * in `issue-create.service.ts` and `status-out.service.ts`; grep for the other three found the
 * wizard, the API's serializer, and nothing else.
 *
 * ⚠️ **Which makes a switch that lies worse than a missing feature.** Somebody turning
 * *worklogIn* off and watching hours keep arriving learns that the settings screen cannot be
 * trusted, and there is no error anywhere to lead them to why. Removing them makes the screen
 * describe what the product does: issues and hours come in, always, and what you choose is what
 * goes back out. Product owner's call, 4 Sep 2026.
 */
exports.SYNC_RULES = ['taskOut', 'statusBoth'];
exports.syncRulesSchema = zod_1.z.object({
    taskOut: zod_1.z.boolean(),
    statusBoth: zod_1.z.boolean(),
});
exports.INTEGRATION_STATES = ['setup', 'active', 'paused', 'error'];
exports.INTEGRATION_AUTH_KINDS = ['token', 'oauth'];
exports.CONFLICT_WINNERS = ['remote', 'local'];
/**
 * ⚠️⚠️ **`SYNC_INTERVALS` IS GONE, AND ITS ABSENCE IS THE POINT.** It offered 5, 15 and 60
 * minutes and P2-24 made all three illegal: the webhook is the data path, and a full reconciliation
 * runs once a day and never more often. There is no interval left to choose between — only *when*
 * the daily run happens.
 *
 * ⚠️ A wall-clock `HH:MM` in the WORKSPACE's timezone, not an instant. It is an intention
 * (*"about 3am"*), which is the third temporal kind `hard-won.md` warns not to convert generically.
 */
exports.overwatchAtSchema = zod_1.z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/u, 'a time of day, HH:MM');
/** The hours the wizard offers. A once-a-day run does not need a minute. */
exports.OVERWATCH_HOURS = Array.from({ length: 24 }, (_unused, hour) => `${String(hour).padStart(2, '0')}:00`);
/**
 * ⚠️ **`webhook` and `retry` are P2-24's, and without them the run log cannot describe the
 * primary data path** — it would show one scheduled row a day for an integration doing most of its
 * work between them, which looks complete and is not.
 */
/**
 * ⚠️ **`backfill` is a run over a window somebody chose by hand** (`0199`) — from the instance's
 * start date forward, usually, to fill the gap the rolling window never reaches. It is its own
 * trigger so the log can say so; `manual` stays what the *Sync recent* button starts.
 */
/** ⚠️ J-8 · `target` is one remote project pulled whole because a line item targets it (`0245`). */
exports.RUN_TRIGGERS = [
    'scheduled',
    'manual',
    'webhook',
    'retry',
    'backfill',
    'target',
];
exports.RUN_STATES = ['running', 'ok', 'partial', 'error'];
exports.RUN_DIRECTIONS = ['in', 'out'];
exports.RUN_RESULTS = ['ok', 'skip', 'error'];
/**
 * The six wizard steps, in the mockup's order (`INTEG_STEPS`).
 *
 * ⚠️ **The order is load-bearing, not decoration.** Step 3 maps statuses and step 4 maps people, and
 * both need what step 1 discovered from the remote system — which is why *"edit just this step"*
 * (`igWizOpen(..., single)`) addresses a step by INDEX in the mockup and by name here. A name
 * survives an inserted step; an index does not.
 */
exports.WIZARD_STEPS = [
    'connection',
    'tasks',
    'statuses',
    /**
     * ⚠️ **The eighth, 27 Oct 2026 (J-5), and it sits beside the step it is the twin of.** A
     * tracker priority is mapped exactly as a tracker status is — a picker of theirs, a picker of
     * ours, one default per group — and the step is drawn by the same component with the nouns
     * swapped. By name, for the reason the comment below gives: adding it moved nothing.
     */
    'priorities',
    /**
     * ⚠️⚠️ **`people` CAME BACK - item 67, and the note it replaces was RIGHT about everything
     * except when.** That note read: *"pairing a remote account with a member is not a setting - it
     * is an ongoing fact about people that changes when somebody joins either system, months after
     * any wizard was last opened"*, and every word of that is still true. What it got wrong is that
     * connecting an instance is exactly when the pairing is worth doing: every remote name is
     * unrecognised at that moment, the list is at its longest, and an unrecognised remote user is an
     * hour that cannot be stored at all.
     *
     * ⚠️⚠️ **SO IT IS BOTH SURFACES, NOT A MOVE BACK.** People management keeps its pairing - that
     * is where the ongoing fact belongs, and where somebody joining next month is dealt with. This
     * step is the first pass. Both edit the same rows through the same endpoint, so there is no
     * second notion of a pairing to reconcile.
     */
    'people',
    /**
     * ⚠️⚠️ **THE SEVENTH, INSERTED HERE, AND THE COMMENT ABOVE IS WHY THAT IS SAFE** (P2-20). The
     * mockup addresses a step by INDEX; this list addresses it by name precisely so that adding one
     * moves nothing. It sits after `people` because it needs what the connection discovered, and
     * before `rules` because a rule switch is the last decision somebody makes.
     */
    'fields',
    'rules',
    'review',
];
exports.PROVIDER_HINTS = {
    jira: {
        urlExample: 'https://ceged.atlassian.net',
        queryExample: 'project in (HSO, ROSS) AND updated >= -30d',
        needsEmail: true,
        defaultStatuses: ['To Do', 'In Progress', 'In Review', 'Done'],
        defaultPriorities: ['Highest', 'High', 'Medium', 'Low', 'Lowest'],
    },
    youtrack: {
        urlExample: 'https://ceged.youtrack.cloud',
        /**
         * ⚠⚠ **THE PREVIOUS EXAMPLE DID NOT PARSE, AND AN EXAMPLE THAT 400s TEACHES A WRONG SYNTAX.**
         * It read `updated: -30d .. Today`; measured against a real site on 4 Sep 2026, YouTrack rejects
         * a relative offset in a date range with `invalid_query` — `#Unresolved` alone is fine and an
         * absolute range is fine, so it is the `-30d` that is not. Anybody who copied the placeholder got
         * a refusal from the one field on the screen that is supposed to be optional.
         */
        queryExample: 'project: HSO #Unresolved updated: 2026-08-01 .. Today',
        needsEmail: false,
        defaultStatuses: ['Open', 'In Progress', 'To Verify', 'Fixed'],
        defaultPriorities: ['Show-stopper', 'Critical', 'Major', 'Normal', 'Minor'],
    },
};
function providerHints(extensionId) {
    return exports.PROVIDER_HINTS[extensionId] ?? null;
}
exports.statusMapEntrySchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    remoteStatus: zod_1.z.string(),
    /** ⚠️ Null is the meaningful case: *"issues in this status do not come across"*. */
    taskStatusId: zod_1.z.uuid().nullable(),
    taskStatusName: zod_1.z.string().nullable(),
    taskStatusColor: zod_1.z.string().nullable(),
    /** ⚠️ The one written outbound for this group (P2-22). Meaningless while `taskStatusId` is null. */
    isDefault: zod_1.z.boolean(),
});
/**
 * J-5 · the status map's twin, for priorities.
 *
 * ⚠️⚠️ **NULL MEANS SOMETHING DIFFERENT HERE, AND THE DIFFERENCE IS THE WHOLE REASON THIS IS NOT
 * THE SAME SCHEMA WITH A RENAMED KEY.** A status mapped to nothing keeps its issues OUT of the sync;
 * a priority mapped to nothing lets the issue in and says *"this tracker priority does not drive
 * ours"* — the task keeps whatever priority Forecastify gave it. A priority is a label on work, not
 * a decision about whether the work exists, and refusing an issue over one would lose a task because
 * somebody had not finished a picker.
 */
exports.priorityMapEntrySchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    remotePriority: zod_1.z.string(),
    taskPriorityId: zod_1.z.uuid().nullable(),
    taskPriorityName: zod_1.z.string().nullable(),
    taskPriorityColor: zod_1.z.string().nullable(),
    /** ⚠️ The one written outbound for this group. Meaningless while `taskPriorityId` is null. */
    isDefault: zod_1.z.boolean(),
});
/**
 * One remote account, and the member it is — or is not — paired with.
 *
 * ⚠️⚠️ **AN IDENTITY IS NOT A CREDENTIAL** (`jira.md` §9.10). This answers *whose hour is
 * this?*: it holds no secret, the person need never have signed in, and it is keyed on the remote
 * SITE so two instances pointing at one Jira share it. *May we act as this person?* is a separate
 * thing, with a different lifetime, that C37 builds.
 *
 * ⚠️ **`remoteEmail` is nullable now, and that is the correction.** The old shape had it
 * `not null` and used it as the key; §1.6 found that Jira returns no email for most users. A key we
 * usually cannot fill is not a key — it is a matching hint.
 *
 * ⚠️ **`memberId` null is the expensive state**, not a tidy default: `worklogs.member_id` is
 * `not null`, so an unpaired identity is an hour that cannot be stored at all.
 */
exports.userIdentitySchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    remoteUserId: zod_1.z.string(),
    remoteEmail: zod_1.z.string().nullable(),
    remoteName: zod_1.z.string(),
    memberId: zod_1.z.uuid().nullable(),
    memberName: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **`oauth` IS THE ONLY ONE THAT IS NOT A GUESS, AND IT IS READ-ONLY** (`0085`). `email`
     * compares two strings and is wrong the moment somebody has two addresses; `manual` is an
     * administrator choosing from a list. `oauth` is the account holder having consented and
     * Atlassian naming them — so the screen shows it and offers no control, and the server refuses a
     * change rather than trusting the screen to.
     */
    matchedBy: zod_1.z.enum(['email', 'manual', 'oauth']).nullable(),
});
exports.integrationInstanceSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    /** `integration_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('integration'),
    extensionId: zod_1.z.enum(catalogue_1.EXTENSION_IDS),
    name: zod_1.z.string(),
    baseUrl: zod_1.z.string(),
    authKind: zod_1.z.enum(exports.INTEGRATION_AUTH_KINDS),
    authEmail: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **THE LAST FOUR CHARACTERS, AND THE ONLY THING THE SECRET EVER PRODUCES OUTBOUND.** There
     * is no field on this schema that could carry a token, which is the point: a screen cannot leak
     * what it was never given, and a future endpoint cannot forget to strip a field that does not
     * exist.
     */
    authHint: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **WHETHER A SECRET EXISTS, WHICH IS NOT THE SAME QUESTION AS `authHint`.** The wizard used
     * the hint as the proxy — *"there is a hint, so there is a token"* — and it is only a display
     * value: any path that stores a secret without computing one (the dev seed, an import, a repair
     * script) leaves the hint null and the secret real. The symptom is the connection step refusing
     * to save an instance that is working perfectly, demanding a token nobody needs to retype.
     */
    hasSecret: zod_1.z.boolean(),
    state: zod_1.z.enum(exports.INTEGRATION_STATES),
    query: zod_1.z.string(),
    sinceOn: wireDateSchema,
    conflictWinner: zod_1.z.enum(exports.CONFLICT_WINNERS),
    overwatchAt: exports.overwatchAtSchema,
    /** ⚠️ Jira's `cloudId`, YouTrack's normalised base URL. Null until C34 connects and learns it. */
    remoteSiteId: zod_1.z.string().nullable(),
    rules: exports.syncRulesSchema,
    lastRunAt: zod_1.z.string().nullable(),
    /**
     * How many of this workspace's projects are linked to this instance - item 67.
     *
     * ⚠️⚠️ **THE INSTANCE CARD RANKED STATUSES AND PEOPLE AND SAID NOTHING ABOUT PROJECTS, WHICH IS
     * THE ONE THAT DECIDES WHETHER ANYTHING SYNCS AT ALL.** An unlinked project imports nothing -
     * `overwatch.service.ts` writes an `unlinked_project` skip and moves on - so an instance with a
     * green connection, mapped statuses, mapped people and zero linked projects is a working
     * integration that will never do anything, and nothing on the screen said so.
     *
     * ⚠️ **Counted in SQL beside the row**, not fetched per card: the instances list draws one
     * card each and a count per card would be N+1 reads on the screen somebody opens first.
     */
    linkedProjectCount: zod_1.z.number().int().nonnegative(),
    /**
     * ┌──────────────────────────────────────────────────────────────────┐
     * │ C39 · live updates. Four facts, and no secret among them.        │
     * └──────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **THE SIGNING SECRET AND THE URL TOKEN ARE BOTH ABSENT FROM THIS SCHEMA ON PURPOSE**, for
     * `authHint`'s reason one field above: either of them is a working key to this workspace's data,
     * and a screen cannot leak what it was never given. `webhookUrl` carries the token, so it is
     * ⚠️ **the origin only** — enough to answer *"which deployment is Jira posting to?"* and nothing
     * more.
     */
    webhook: zod_1.z
        .object({
        /** Where Jira was told to post, as an origin. Null when nothing is registered. */
        origin: zod_1.z.string().nullable(),
        registeredAt: zod_1.z.string().nullable(),
        /**
         * ⚠️ **When Jira last reached us, which is the only evidence the subscription is alive.** An
         * admin can delete it at any time and nothing tells us; silence is the only symptom.
         */
        lastDeliveryAt: zod_1.z.string().nullable(),
        /**
         * ⚠️⚠️ **A CLIMBING COUNT IS EITHER A ROTATED SECRET OR SOMEBODY PROBING**, and both belong
         * in front of a person. Zero for almost every instance for ever.
         */
        rejectedCount: zod_1.z.number().int().nonnegative(),
        /**
         * ⚠️⚠️ **`moved` IS THE ONE THIS EXISTS FOR.** Jira posts to the address it was given, so a
         * deployment that changes its public origin — or a tunnel that restarts on a new one — keeps
         * a subscription pointing somewhere nothing answers. **Nothing on our side notices, because
         * nothing arrives**, and the symptom is identical to an admin having deleted it. The server
         * compares the registered origin with its own and says which.
         *
         * ⚠️ **There is deliberately no `quiet` state here.** A lapse and a genuinely quiet Jira look
         * the same from a timestamp, and §9.2 says the honest detector is DRIFT — the overwatch
         * finding changes nobody announced — which is not measured yet. Guessing from silence would
         * cry wolf on every low-traffic project.
         */
        state: zod_1.z.enum(['off', 'live', 'moved', 'stale']),
        /**
         * ⚠️⚠️ **A HAND-REGISTERED SUBSCRIPTION CANNOT BE REPAIRED FROM HERE** (§9.1's fallback, for a
         * client who will not grant Jira admin). We hold no id for it and no credential that could
         * change it, so *"reconnect"* is advice for a person rather than a button that works — and a
         * screen that offered the button anyway would fail in a way nobody could explain.
         */
        byHand: zod_1.z.boolean(),
        /**
         * ⚠️⚠️ **DRIFT IS THE HONEST LAPSE DETECTOR, AND §9.2 SAYS SO**: *"everything the overwatch
         * finds that a webhook should have delivered is drift, and drift is a signal."* This is what
         * the last reconciliation applied **that no delivery ever mentioned** (`0083`) — so a healthy
         * subscription sits at zero, and anything else means deliveries are not arriving whatever the
         * timestamps say.
         *
         * ⚠️ **An earlier version counted everything the reconciliation applied, and that was wrong in
         * the direction that matters**: it is legitimately non-zero on a perfectly healthy
         * integration — the daily window reaches back further than the webhook has existed, a project
         * linked yesterday backfills, an hour arrives for an identity only just paired. A permanent
         * amber warning is a warning nobody reads.
         */
        driftLastRun: zod_1.z.number().int().nonnegative().nullable(),
        /**
         * ⚠️⚠️ **WHEN THE CURRENT UNBROKEN RUN OF DRIFT BEGAN, WHICH IS THE QUESTION SUPPORT OPENS
         * WITH.** *"Is it healthy now"* is answered by the number above; *"when did it stop"* is not,
         * and that is the one somebody actually asks. Null while the last reconciliation was clean.
         *
         * ⚠️ **The CURRENT streak, not the first bad run ever** — an integration that had a bad week
         * in July and has been clean since is not broken, and dating it from July would say it was.
         */
        driftSince: zod_1.z.string().nullable(),
    })
        .nullable(),
    statuses: zod_1.z.array(exports.statusMapEntrySchema),
    priorities: zod_1.z.array(exports.priorityMapEntrySchema),
    users: zod_1.z.array(exports.userIdentitySchema),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️⚠️ **THE ONLY SHAPE IN THIS FILE THAT CARRIES A SECRET, AND IT CROSSES THE WIRE EXACTLY ONCE.**
 * §9.1's manual fallback exists for a client whose security team will not grant Jira admin to a
 * third party — so a person has to type the signing secret into Jira's admin UI themselves, which
 * means it must leave the server once. Every other read of this instance gives back the URL and
 * never this. Asking again rotates both and invalidates whatever was already pasted.
 */
/**
 * What the remote system calls its statuses, offered to the mapping step.
 *
 * ⚠️⚠️ **THE STEP USED TO ASK SOMEBODY TO TYPE THESE.** C34 seeded it from a hard-coded guess — *To
 * Do, In Progress, In Review, Done* — with a comment admitting it was a starting point rather than
 * a claim about what is over there. A name that does not match theirs exactly is a transition Jira
 * never offers: refused, counted, abandoned after five, and nothing anywhere says why.
 *
 * ⚠️ **`category` earns its place**: *Done* and *Cancelled* are both terminal, and somebody mapping
 * fifteen statuses has no other way to tell one kind of ending from another at a glance.
 */
exports.remoteStatusChoiceSchema = zod_1.z.object({
    name: zod_1.z.string(),
    category: zod_1.z.enum(['new', 'indeterminate', 'done', 'unknown']),
});
/**
 * What the remote system calls its priorities, offered to the mapping step — in the tracker's own
 * order, which on both providers is most urgent first.
 *
 * ⚠️ **No category.** Jira grades a status; it ranks a priority, and the rank IS the order of the
 * list. Nothing a second column could add that the position does not already say.
 */
exports.remotePriorityChoiceSchema = zod_1.z.object({
    name: zod_1.z.string(),
});
/**
 * ux-pass-2 · an issue type a line item's target can create — *Írás a hibakövetőbe* lets a person
 * choose it (`GET /integrations/:id/remote-projects/:remoteProjectId/issue-types`).
 *
 * ⚠️ **`subtask` travels rather than being filtered out**, for the port's own reason: it is a fact
 * about the remote project. The picker draws one disabled — Jira refuses a sub-task created without
 * a parent, and an outbound task has none.
 */
exports.remoteIssueTypeSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    subtask: zod_1.z.boolean(),
});
exports.webhookManualSetupSchema = zod_1.z.object({
    url: zod_1.z.string(),
    secret: zod_1.z.string(),
    events: zod_1.z.array(zod_1.z.string()),
    /** The restricted JQL to paste into *Issue related events*. Ids, never keys (§8.4). */
    filter: zod_1.z.string(),
});
exports.integrationRunSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    instanceId: zod_1.z.uuid(),
    startedAt: zod_1.z.string(),
    finishedAt: zod_1.z.string().nullable(),
    durationMs: zod_1.z.number().int().nullable(),
    trigger: zod_1.z.enum(exports.RUN_TRIGGERS),
    state: zod_1.z.enum(exports.RUN_STATES),
    inTasks: zod_1.z.number().int(),
    inWorklogs: zod_1.z.number().int(),
    outTasks: zod_1.z.number().int(),
    outWorklogs: zod_1.z.number().int(),
    errors: zod_1.z.array(zod_1.z.string()),
    /**
     * ⚠️ **The window the run asked the tracker for** (`0199`) — instants, on the tracker's own
     * *updated* clock. Null on a webhook delivery or a retry sweep; `windowTo` null when the run read
     * up to the moment it ran. A backfill row without these would say *backfill* and nothing else.
     */
    windowFrom: zod_1.z.string().nullable(),
    windowTo: zod_1.z.string().nullable(),
    /**
     * J-8 · the remote project a `target` run read (`0245`), and what the tracker currently calls
     * it — null on every other kind of run. A row that says *target* and nothing else does not
     * answer *which project is it on now?*, which is the question the Syncs tab is opened with.
     */
    remoteProjectId: zod_1.z.string().nullable(),
    remoteProjectKey: zod_1.z.string().nullable(),
});
/**
 * What *Sync recent* and the ranged dialog post. Wire dates, both optional: nothing at all means
 * *recent* — one month back to now, trigger `manual`; a `from` makes it a `backfill`.
 *
 * ⚠️ **The range is on the tracker's CHANGE time, not the day the hours were worked** — the only
 * feed either provider offers is *what changed since*. A June hour edited in September is a
 * September change. The dialog says so; this comment is where the rule lives.
 */
exports.syncRunRequestSchema = zod_1.z
    .object({
    from: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/u)
        .optional(),
    to: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/u)
        .optional(),
})
    .refine((body) => body.from === undefined || body.to === undefined || body.from <= body.to, {
    message: 'from must not be after to',
    path: ['to'],
});
/**
 * What a sync request answers, now that the run is QUEUED rather than awaited: the run row, in the
 * `running` state, and whether this request created it. `queued: false` means one of this kind was
 * already going and the row is that one — the answer to *sync this* is *it is*.
 */
exports.syncRunResponseSchema = zod_1.z.object({
    run: exports.integrationRunSchema,
    queued: zod_1.z.boolean(),
});
/**
 * One line of a run's detail.
 *
 * ⚠️⚠️ **THE LAST THREE FIELDS ARE ITEM 67, AND THEY ARE WHAT MAKES THIS LOG USABLE.** A record
 * was a key and a verb - `TASK - PROJ-412 - IN - UPDATED` - and the question people open the log
 * with is always some form of *"where did this go?"*: which task did this hour land on, which
 * project does this issue belong to, and what did the remote system actually send. None of it was
 * here, so diagnosing one wrong hour meant searching the product by hand for a task whose title
 * nobody had told them.
 */
exports.integrationRunRecordSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    entity: zod_1.z.string(),
    remoteKey: zod_1.z.string(),
    direction: zod_1.z.enum(exports.RUN_DIRECTIONS),
    action: zod_1.z.string(),
    result: zod_1.z.enum(exports.RUN_RESULTS),
    note: zod_1.z.string(),
    /**
     * What crossed the wire, trimmed by the writer.
     *
     * ⚠️⚠️ **`z.unknown()`, AND A HAND-WRITTEN SCHEMA HERE WOULD BE WRONG BY CONSTRUCTION.** This is
     * a remote system's object, not ours: its shape is Jira's or YouTrack's, it differs per
     * provider, per site configuration and per custom field, and it changes when a vendor ships. A
     * schema that described it would refuse a valid payload the first time somebody added a field to
     * their tracker. The screen renders it as formatted JSON, which is the only honest rendering of
     * a shape we do not own.
     *
     * ⚠️ **`null` for every record written before `0103`**, and for every record that touched
     * nothing.
     */
    payload: zod_1.z.unknown().nullable(),
    /**
     * ⚠️⚠️ **RESOLVED AT READ TIME, SO IT NAMES THE ROW AS IT IS NOW.** `0103` stores only the id;
     * the service joins. That is deliberate for a log somebody reads in order to go and look at
     * something - a task renamed since the sync is still the task they want, under its current name.
     *
     * ⚠️ **Nullable, and it is the ordinary case on a skipped record**: nothing was touched, so
     * there is nothing to name. It is also null for anything the log wrote before `0103`, and for a
     * task somebody has since deleted - the log outlives what it logged, which is why the column
     * carries no foreign key.
     */
    task: zod_1.z.object({ id: zod_1.z.uuid(), title: zod_1.z.string() }).nullable(),
    /** The project behind that task. A worklog resolves through its task to get here. */
    project: zod_1.z.object({ id: zod_1.z.uuid(), name: zod_1.z.string() }).nullable(),
});
exports.integrationRunDetailSchema = exports.integrationRunSchema.extend({
    /**
     * ⚠️⚠️ **ONE PAGE OF THEM, 30 Oct 2026 — `?limit=` and `?offset=` on the detail.** A backfill over
     * three years of Jira writes tens of thousands of records, and the detail used to send all of
     * them: thirty seconds to open and a page a mile long.
     */
    records: zod_1.z.array(exports.integrationRunRecordSchema),
    /** ⚠️ **ADDED UNDER RULE 8.** The list meta of `records` — `total` is every record of the run. */
    recordsMeta: pagination_1.listMetaSchema,
});
/**
 * ⚠️⚠️ **`https` OR `http`, AND A HOST WITH A DOT — the mockup's own check, and it is the useful
 * one.** `z.string().url()` accepts `http://localhost` and `https://x`, which are not remote
 * systems anybody is integrating with; the failure of the loose version is a saved instance that
 * cannot possibly connect and a support call in C34.
 */
const remoteUrlSchema = zod_1.z
    .string()
    .trim()
    .regex(/^https?:\/\/[^\s/]+\.[^\s/]+/u, 'must be a full URL with a host');
/**
 * ⚠️ **The secret is `optional`, never `nullable`, and the difference carries meaning.** Absent
 * means *"leave whatever is stored"* — which is how the wizard can reopen an existing instance
 * without the browser ever holding the token. An explicit empty string is refused, so there is no
 * way to accidentally blank a working credential by submitting a form with a placeholder in it.
 */
const authSecretSchema = zod_1.z.string().min(1).max(4096);
const statusGroupRow = (entry) => ({ groupId: entry.taskStatusId, isDefault: entry.isDefault });
const priorityGroupRow = (entry) => ({ groupId: entry.taskPriorityId, isDefault: entry.isDefault });
/**
 * The one-default-per-group rule, over either map (P2-22, J-5).
 *
 * ⚠️⚠️ **A GROUP OF TWO OR MORE WITH NO DEFAULT HAS AN UNDEFINED OUTBOUND WRITE**, and the partial
 * unique index cannot catch it: an index forbids two defaults, not zero. A group of exactly one
 * needs no flag — the default is its only member. An unmapped row belongs to no group.
 *
 * Answers `group_needs_default:<id>` and `group_one_default:<id>`, which the caller prefixes with
 * the map's noun. ⚠️ Exported so the wizard's client-side twin runs the same function rather than a
 * copy of it — `status-groups.test.ts` pins the two together.
 */
function groupDefaultProblems(rows) {
    const groups = new Map();
    for (const row of rows) {
        if (row.groupId === null)
            continue;
        const seen = groups.get(row.groupId) ?? { total: 0, defaults: 0 };
        seen.total += 1;
        if (row.isDefault)
            seen.defaults += 1;
        groups.set(row.groupId, seen);
    }
    const problems = [];
    for (const [groupId, seen] of groups) {
        if (seen.total > 1 && seen.defaults === 0)
            problems.push(`group_needs_default:${groupId}`);
        if (seen.defaults > 1)
            problems.push(`group_one_default:${groupId}`);
    }
    return problems;
}
exports.saveIntegrationRequestSchema = zod_1.z
    .object({
    extensionId: zod_1.z.enum(catalogue_1.EXTENSION_IDS),
    name: zod_1.z.string().trim().min(1).max(80),
    baseUrl: remoteUrlSchema,
    authKind: zod_1.z.enum(exports.INTEGRATION_AUTH_KINDS),
    authEmail: zod_1.z.email().max(320).optional(),
    authSecret: authSecretSchema.optional(),
    query: zod_1.z.string().max(2000).default(''),
    sinceOn: wireDateSchema,
    conflictWinner: zod_1.z.enum(exports.CONFLICT_WINNERS),
    overwatchAt: exports.overwatchAtSchema,
    rules: exports.syncRulesSchema,
    statuses: zod_1.z.array(zod_1.z.object({
        remoteStatus: zod_1.z.string().trim().min(1).max(80),
        taskStatusId: zod_1.z.uuid().nullable(),
        /** ⚠️ The one written outbound for this group. At most one per Forecastify status. */
        isDefault: zod_1.z.boolean().default(false),
    })),
    /**
     * J-5 · the priority map, replaced whole like the status map.
     *
     * ⚠️ **Defaulted to empty rather than required**, so a client written before the map existed
     * — the MCP bridge, a saved request replayed from a test — still saves, with the one meaning an
     * empty map has: no tracker priority drives ours.
     */
    priorities: zod_1.z
        .array(zod_1.z.object({
        remotePriority: zod_1.z.string().trim().min(1).max(80),
        taskPriorityId: zod_1.z.uuid().nullable(),
        isDefault: zod_1.z.boolean().default(false),
    }))
        .default([]),
    /** Absent on create; required on update, where a stale write is a 409 like everywhere else. */
    version: zod_1.z.number().int().optional(),
})
    .superRefine((value, ctx) => {
    /**
     * ⚠️⚠️ **THE SERVER RE-CHECKS WHAT THE WIZARD'S FIRST STEP CHECKS.** `igStepError` runs in the
     * mockup's browser and nowhere else; rule 6 says UI gating is UX. Both rules below are about a
     * credential being *complete*, and an incomplete one produces a 401 in C34 rather than a
     * validation error here — which is a much worse place to find out.
     */
    /**
     * ⚠️⚠️ **A GROUP OF TWO OR MORE WITH NO DEFAULT HAS AN UNDEFINED OUTBOUND WRITE**
     * (P2-22), and `0063`'s partial unique index cannot catch it: an index forbids two defaults, not
     * zero. A group of exactly one needs no flag — the default is its only member.
     */
    for (const problem of groupDefaultProblems(value.statuses.map(statusGroupRow))) {
        ctx.addIssue({ code: 'custom', path: ['statuses'], message: `status_${problem}` });
    }
    /* ⚠️ J-5 · the same arithmetic over the priority map, with its own message prefix so the
       wizard can send somebody to the right step. */
    for (const problem of groupDefaultProblems(value.priorities.map(priorityGroupRow))) {
        ctx.addIssue({ code: 'custom', path: ['priorities'], message: `priority_${problem}` });
    }
    if (value.authKind !== 'token')
        return;
    const hints = providerHints(value.extensionId);
    if (hints?.needsEmail === true && (value.authEmail ?? '') === '') {
        ctx.addIssue({
            code: 'custom',
            path: ['authEmail'],
            message: 'this provider attributes the token to an account, so the email is required',
        });
    }
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C34 · discovery.                                                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A PROBE STORES NOTHING AND RUNS ON TYPED-IN CREDENTIALS**, because it happens at the
 * connection step before an instance exists. Its whole job is `jira.md` §9.1's *prove what you can
 * see*: the admin credential's visibility is the silent ceiling on the integration, and the moment
 * somebody types it is the only cheap place to find out how high it is.
 */
exports.probeRequestSchema = zod_1.z.object({
    extensionId: zod_1.z.enum(catalogue_1.EXTENSION_IDS),
    baseUrl: remoteUrlSchema,
    authEmail: zod_1.z.email().max(320).optional(),
    /* ⚠️ Inbound only, once, exactly like a save. Nothing here ever comes back out. */
    authSecret: authSecretSchema,
});
exports.probeResultSchema = zod_1.z.object({
    /** Jira's `cloudId`. ⚠️ Never the base URL — a customer can change that (§9.10). */
    siteId: zod_1.z.string(),
    /**
     * ⚠️⚠️ **WHO THE CREDENTIAL AUTHENTICATED AS — AND THE PROOF THAT IT AUTHENTICATED AT ALL.**
     * Added 8 Sep 2026, after a **revoked** Jira token produced a green connection over an empty
     * project list. On Jira Cloud both calls the proof rested on answer 200 to an anonymous caller
     * (`/_edge/tenant_info` with a real `cloudId`, `/rest/api/3/project/search` with `total: 0`), so
     * *the token is dead* and *this account can browse nothing* arrived at the wizard as the same
     * screen. `RemoteReader.whoAmI` carries the measurement.
     *
     * ⚠️ **Shown, not merely checked.** An Atlassian API token is ACCOUNT-scoped, so the commonest
     * real mistake at this step is a personal token where a service account was meant — which stays
     * invisible until that person leaves. A name and an address beside the tick is how somebody
     * notices at the only moment it is cheap to change.
     */
    account: remote_1.remoteUserSchema,
    /**
     * ⚠️ **The list, not a count.** *"Four of the eleven you expected"* is only actionable if the
     * reader can see WHICH four — a number alone tells somebody they have a problem and nothing
     * about where it is.
     */
    projects: zod_1.z.array(zod_1.z.object({
        remoteProjectId: zod_1.z.string(),
        key: zod_1.z.string(),
        name: zod_1.z.string(),
        archived: zod_1.z.boolean(),
    })),
    /**
     * ⚠️⚠️ **THE SITE'S STATUSES COME BACK WITH THE PROOF, WHICH REMOVES A WHOLE ROUND TRIP THROUGH
     * THE WIZARD.** `GET /integrations/:id/statuses` needs a SAVED instance, so before this the
     * Statuses step on a new connection had nothing to offer and somebody had to finish the wizard,
     * reopen it, and come back — the *"instead of having to come back"* the product owner asked for
     * on 3 Sep 2026.
     *
     * ⚠️ One extra request on a button somebody pressed deliberately, against a saved step later.
     */
    statuses: zod_1.z.array(exports.remoteStatusChoiceSchema),
    /** J-5 · and the priorities, on the same terms as the statuses: one request, failure tolerated. */
    priorities: zod_1.z.array(exports.remotePriorityChoiceSchema),
    /**
     * ⚠️⚠️ **AND THE CUSTOM FIELDS TOO, FOR THE SAME REASON AND FROM THE SAME COMPLAINT.** The
     * statuses moved onto the proof on 3 Sep 2026 because the Statuses step had nothing to offer until
     * the wizard had been finished, reopened and returned to. The Fields step had *exactly* the same
     * shape and was not noticed until a YouTrack instance hit it — `GET /integrations/:id/fields`
     * needs a saved instance, so the step said *save first* and a person mapping custom fields had to
     * do the round trip the other step no longer needs.
     *
     * ⚠️ **A failure here is not a failed test**, exactly as with statuses: reading fields needs its
     * own permission on both providers, and a credential that can browse projects but not enumerate
     * custom fields is a working connection with one step left to fill in by hand.
     */
    fields: zod_1.z.array(remote_1.remoteFieldSchema),
    /**
     * ⚠️ **Whether `fields` is the whole answer** (11 Oct 2026). The per-project field walk is one
     * request per issue type per project, and on a site with 380 projects it outlasted the reverse
     * proxy's 60 s — so the proof walks a few projects at a time under a deadline and says here when
     * the deadline cut it. The saved instance's Fields step reads the rest.
     */
    fieldsComplete: zod_1.z.boolean(),
    /**
     * ⚠️⚠️ **AND THE ACCOUNTS, WHICH IS THE THIRD TIME THIS EXACT SHAPE HAS BEEN REPORTED.** The
     * statuses moved onto the proof on 3 Sep 2026 and the fields followed it; the People step — back
     * since item 67 — had the same problem and the same complaint: *"when I connect, the people list
     * doesn't load until I save the instance, why not? can't we load this right away?"*
     *
     * ⚠️⚠️ **A PREVIEW, NOT PAIRINGS.** An identity row is keyed to an instance, so before the
     * instance exists there is nothing to pair TO and nothing to store a pairing ON. What the wizard
     * can honestly do at this point is show WHO is over there, so somebody sees the roster is the one
     * they expected before they commit to the connection; the pickers arrive with the saved instance.
     * Anything more would mean inventing rows for an instance that may never be created.
     *
     * ⚠️ **Capped, and a failure here is not a failed test**, like the two above it: enumerating
     * users is its own permission on both providers, and a site with four thousand accounts must not
     * turn a connection test into a page-through of all of them.
     */
    people: zod_1.z.array(remote_1.remoteUserSchema),
});
exports.discoverResultSchema = zod_1.z.object({
    siteId: zod_1.z.string(),
    projectCount: zod_1.z.number().int(),
    peopleCount: zod_1.z.number().int(),
    matchedCount: zod_1.z.number().int(),
    /**
     * ⚠️⚠️ **THE ONE THE INSTANCE CARD RANKS FIRST** (§9.10). An unrecognised remote user is an hour
     * that cannot be stored at all, so this number is the difference between a forecast that is short
     * and a forecast nobody can explain.
     */
    unpairedCount: zod_1.z.number().int(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C34 · what discovery staged: a remote project the credential can see.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **NEVER AN IMPORT** (`jira.md` §9.5). A remote container we have never seen produces **no**
 * Forecastify project — it produces a row a line item may target (P2-32). Whether a client's Jira
 * can fill this workspace with projects nobody chose is the difference.
 */
exports.stagedProjectSchema = zod_1.z.object({
    remoteProjectId: zod_1.z.string(),
    key: zod_1.z.string(),
    name: zod_1.z.string(),
    archived: zod_1.z.boolean(),
    discoveredAt: zod_1.z.string(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ P2-32 · one line item that collects from one of this instance's projects.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A FACT, NOT A CONTROL.** The target is set on the line item's own page, where its rules
 * are; this row is how an administrator sees, for one tracker, every line item that reads from it
 * — including several on one remote project, which is the case the old one-to-one links forbade.
 */
exports.instanceTargetSchema = zod_1.z.object({
    lineItemId: zod_1.z.uuid(),
    /** The contract's code, or its identifier while it has none — the first half of its address. */
    contractLabel: zod_1.z.string(),
    /** The line item's ordinal inside its contract — the second half (`/contracts/HSO-C01/2`). */
    number: zod_1.z.number().int(),
    /**
     * R-57 · the contract's and the line item's public identifiers — what the screen shows and
     * links to (`/contracts/cont_…/li_…`). The label and number above stay on the wire.
     */
    contractIdentifier: (0, identifier_1.identifierSchema)('contract'),
    lineItemIdentifier: (0, identifier_1.identifierSchema)('lineItem'),
    lineItemName: zod_1.z.string(),
    lineItemStatus: zod_1.z.enum(['open', 'closed']),
    /** `rule` collects by its conditions; `manual` holds only what people put on it. */
    assignment: zod_1.z.enum(['manual', 'rule']),
    /** Null on a client contract, whose line items hold no work. */
    projectName: zod_1.z.string().nullable(),
    remoteProjectId: zod_1.z.string(),
    remoteKey: zod_1.z.string(),
    remoteName: zod_1.z.string(),
    /** `0161`. A fact about the remote project, shown on every line item that targets it. */
    remoteState: zod_1.z.enum(['active', 'archived', 'trashed', 'deleted']),
    /** Whether the credential could still see it at the last discovery. */
    visible: zod_1.z.boolean(),
    /** P2-25, now the line item's (D7). */
    writesEnabled: zod_1.z.boolean(),
});
exports.instanceTargetsSchema = zod_1.z.object({
    /** ⚠️ Null when discovery has never run. *"Nothing here"* and *"never looked"* are different. */
    discoveredAt: zod_1.z.string().nullable(),
    /** P2-32 · every line item targeting one of this instance's remote projects, by remote key. */
    targets: zod_1.z.array(exports.instanceTargetSchema),
    /** P2-32 · what the credential can see that no line item targets. Discovery's other half. */
    untargeted: zod_1.z.array(exports.stagedProjectSchema),
});
/**
 * One remote account and the member it is paired with, as People management shows it.
 *
 * ⚠️ `remoteEmail` is null for most Jira accounts (§1.6), which is why `instanceNames` is here —
 * without an address, the instance name is the only thing that says WHICH system this account is
 * in.
 */
exports.identityRowSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    remoteSiteId: zod_1.z.string(),
    remoteUserId: zod_1.z.string(),
    remoteName: zod_1.z.string(),
    remoteEmail: zod_1.z.string().nullable(),
    memberId: zod_1.z.uuid().nullable(),
    memberName: zod_1.z.string().nullable(),
    /** ⚠️ `oauth` is proof rather than a guess, and the screen offers no control for it (`0085`). */
    matchedBy: zod_1.z.enum(['email', 'manual', 'oauth']).nullable(),
    instanceNames: zod_1.z.array(zod_1.z.string()),
    /**
     * ⚠️ **The provider, for item 48's logo** — `jira`, `youtrack`.
     *
     * ⚠️ `null` when no instance points at the site any more, which is a real state: an identity
     * outlives the instance that discovered it, because it is keyed on the SITE. A row with no
     * provider draws no logo rather than a wrong one.
     */
    provider: zod_1.z.string().nullable(),
});
/** ⚠️ Null unpairs. The endpoint is one verb, because *"who is this?"* has one answer at a time. */
exports.pairIdentityRequestSchema = zod_1.z.object({ memberId: zod_1.z.uuid().nullable() });
exports.linkTaskRequestSchema = zod_1.z.object({
    remoteIssueId: zod_1.z.string().trim().min(1).max(200),
    remoteKey: zod_1.z.string().trim().max(80).optional(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C34 · the `fields` step. P2-20.                                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **What the remote system has that we store nowhere**, and where a workspace chose to put it.
 * The discovered list is read LIVE — a stale cache of somebody's custom fields is a list that
 * offers a field they deleted last week.
 */
exports.fieldStepSchema = zod_1.z.object({
    /** Everything discoverable, with `claimed` already decided by the client (§7.3). */
    discovered: zod_1.z.array(remote_1.remoteFieldSchema),
    /**
     * ⚠️ **Whether `discovered` is the whole answer** (11 Oct 2026): the per-project walk runs under
     * a deadline shorter than the reverse proxy's, like the proof's — see `probeResultSchema`. A
     * saved instance that targets 180 Jira projects does not fit in one step; the step says so.
     */
    discoveredComplete: zod_1.z.boolean(),
    /** What is mapped today, keyed by `remoteFieldId`. */
    maps: zod_1.z.array(zod_1.z.object({
        remoteFieldId: zod_1.z.string(),
        remoteFieldName: zod_1.z.string(),
        remoteType: zod_1.z.string(),
        metafieldId: zod_1.z.uuid(),
        metafieldName: zod_1.z.string(),
    })),
    /** The task metafields a row may point at. ⚠️ `entity: 'task'` only — nothing else can receive. */
    metafields: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.uuid(),
        name: zod_1.z.string(),
        key: zod_1.z.string(),
        type: zod_1.z.enum(metafields_1.METAFIELD_TYPES),
        required: zod_1.z.boolean(),
    })),
    /**
     * ⚠️⚠️ **WHETHER THE CALLER MAY CREATE ONE, WHICH IS A SECOND PERMISSION** (P2-20). Connecting an
     * integration must not be a back door into the workspace's field schema, so creating needs
     * `settings.manage` as well as `integration.manage`. Without it the picker still offers existing
     * fields and only the create button is absent.
     */
    canCreate: zod_1.z.boolean(),
});
/**
 * ⚠️ **The whole list, replaced.** A row the user set back to *don't sync* has to disappear, and a
 * partial update cannot express that.
 */
exports.saveFieldMapsRequestSchema = zod_1.z.object({
    maps: zod_1.z.array(zod_1.z.object({
        remoteFieldId: zod_1.z.string().trim().min(1).max(200),
        remoteFieldName: zod_1.z.string().trim().min(1).max(400),
        remoteType: zod_1.z.string().trim().min(1).max(80),
        /**
         * ⚠️ Either point at one that exists, or ask for one to be made — never both, and never
         * neither. A discriminated union rather than two optional fields, so the impossible
         * combinations cannot be expressed at all.
         */
        target: zod_1.z.discriminatedUnion('kind', [
            zod_1.z.object({ kind: zod_1.z.literal('existing'), metafieldId: zod_1.z.uuid() }),
            zod_1.z.object({
                kind: zod_1.z.literal('create'),
                name: zod_1.z.string().trim().min(1).max(100),
                type: zod_1.z.enum(metafields_1.METAFIELD_TYPES),
                /** ⚠️ For `select` and `multi`, the remote system's own option list. */
                options: zod_1.z.array(zod_1.z.string().min(1).max(100)).max(50).default([]),
            }),
        ]),
    })),
});
/** Pause or resume. The wizard's `state` is the server's business; this is the one switch. */
exports.setIntegrationActiveRequestSchema = zod_1.z.object({
    active: zod_1.z.boolean(),
    version: zod_1.z.number().int(),
});
/**
 * How many of a map's rows are connected — the wizard's *"4 / 6 párosítva"* and the overview's
 * warning, computed once here because three screens print it.
 */
function mappedCount(entries) {
    const mapped = entries.filter((entry) => (entry.taskStatusId ?? entry.taskPriorityId ?? entry.memberId ?? null) !== null).length;
    return { mapped, total: entries.length };
}
/**
 * Which rules are switched on, in the catalogue's order.
 *
 * ⚠️ Used by the overview's chip row and the review step, which would otherwise each iterate the
 * object's keys — and `Object.keys` order is not the order a reader expects to see them in.
 */
function activeRules(rules) {
    return exports.SYNC_RULES.filter((rule) => rules[rule]);
}
/* ── the parked-hours inbox ─────────────────────────────────────────────────────────────────────
 *
 * ⚠️⚠️ **THE ONLY SCREEN IN THE PRODUCT WHERE AN HOUR EXISTS AND BELONGS TO NOBODY.** Everywhere
 * else a worklog has a task and a person because the columns are `not null`; here it has neither
 * yet, and the row is what stops `worklog/updated`'s forward-only feed losing it (`jira.md` §1.4).
 */
exports.PARKED_REASONS = ['no_task', 'no_member'];
exports.parkedWorklogSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    reason: zod_1.z.enum(exports.PARKED_REASONS),
    remoteWorklogId: zod_1.z.string(),
    remoteIssueId: zod_1.z.string(),
    /** 17 Oct 2026 · `ABC-123`, labelled by the sync (`0221`). Null until it has been looked up. */
    remoteIssueKey: zod_1.z.string().nullable(),
    remoteAuthorId: zod_1.z.string().nullable(),
    /** ⚠️ The person's name if we discovered them, so the inbox can say WHO rather than an opaque id. */
    remoteAuthorName: zod_1.z.string().nullable(),
    workedOn: wireDateSchema,
    hours: zod_1.z.number(),
    note: zod_1.z.string().nullable(),
    firstSeenAt: zod_1.z.string(),
    lastSeenAt: zod_1.z.string(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ J-4 · the parked-hours inbox's totals, over ALL of it — not the page.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE TAB SUMMED THE ROWS IT HELD, AND IT HELD 200.** Loginet-2's rehearsal (15 Oct 2026)
 * showed *"700+ hours"* over exactly 200 rows, oldest first — so every hour parked after early
 * January was invisible, and the total was a page's total presented as the inbox's. The rows now
 * page; the numbers come from here.
 *
 * ⚠️ **`authors` is the question somebody opens the inbox with**: *"why are Gábor's hours
 * missing?"* — the ten authors with the most parked hours, with the account id when we never
 * discovered a name, which is itself the diagnosis for `no_member`.
 */
exports.parkedSummarySchema = zod_1.z.object({
    count: zod_1.z.number().int().nonnegative(),
    hours: zod_1.z.number(),
    reasons: zod_1.z.array(zod_1.z.object({
        reason: zod_1.z.enum(exports.PARKED_REASONS),
        count: zod_1.z.number().int().nonnegative(),
        hours: zod_1.z.number(),
    })),
    authors: zod_1.z.array(zod_1.z.object({
        remoteAuthorId: zod_1.z.string().nullable(),
        remoteAuthorName: zod_1.z.string().nullable(),
        count: zod_1.z.number().int().nonnegative(),
        hours: zod_1.z.number(),
    })),
    /**
     * 17 Oct 2026 · the table's filter options: EVERY author with a parked hour (not the top ten),
     * and the managers of those we have paired with a member. ⚠️ An unpaired author has no member
     * and so no manager — the manager filter can only ever match paired people.
     */
    authorOptions: zod_1.z.array(zod_1.z.object({ remoteAuthorId: zod_1.z.string(), remoteAuthorName: zod_1.z.string().nullable() })),
    managerOptions: zod_1.z.array(zod_1.z.object({ memberId: zod_1.z.string(), name: zod_1.z.string() })),
});
/**
 * ⚠️ **Discarding is a decision, not a cleanup**, so it is its own request rather than a delete
 * that says nothing. The feed is site-wide: most of what parks with `no_task` belongs to projects
 * nobody linked, and a person saying *"not ours"* is the only thing that can tell those apart from
 * an issue we simply have not linked yet.
 */
exports.discardParkedRequestSchema = zod_1.z.object({
    ids: zod_1.z.array(zod_1.z.uuid()).min(1).max(200),
});
/**
 * One task's link to one remote issue, as the task screen reads it.
 *
 * ⚠️⚠️ **`remoteKey` AND OUR OWN KEY ARE DIFFERENT NUMBERS THAT LOOK THE SAME.** A Forecastify task
 * key is minted per project by `next_task_number`; the tracker's is its own counter. When a
 * workspace's project prefix happens to match the remote container's key — which it often will,
 * because both are an abbreviation of the same name — `HSO-51` is a valid identifier in both
 * systems pointing at **different work**. Showing only ours is what makes *"but I logged it on
 * HSO-51"* unanswerable from inside the product.
 */
exports.taskIssueLinkSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    taskId: zod_1.z.uuid(),
    remoteIssueId: zod_1.z.string().nullable(),
    remoteKey: zod_1.z.string().nullable(),
    syncState: zod_1.z.string(),
    lastError: zod_1.z.string().nullable(),
    instanceId: zod_1.z.uuid(),
    instanceName: zod_1.z.string(),
    extensionId: zod_1.z.enum(catalogue_1.EXTENSION_IDS),
    baseUrl: zod_1.z.string(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ J-3 · a synced task no line item's rule claims, as the instance lists it.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * It exists — saved, with its hours, under `MISC-n` and no project — and until now nothing listed
 * it outside an alert on the projects collecting from its remote project. The fix is a rule (or a
 * line item picked by hand on the task), so the row carries what a rule is written from: the remote
 * project, the tracker's key, status and components.
 */
exports.unroutedTaskSchema = zod_1.z.object({
    taskId: zod_1.z.uuid(),
    /** Ours — `MISC-488`. ⚠️ It stays when the task is filed later: keys never change. */
    key: zod_1.z.string(),
    title: zod_1.z.string(),
    remoteKey: zod_1.z.string().nullable(),
    remoteStatus: zod_1.z.string().nullable(),
    remoteProjectId: zod_1.z.string().nullable(),
    remoteProjectKey: zod_1.z.string().nullable(),
    remoteProjectName: zod_1.z.string().nullable(),
    components: zod_1.z.array(zod_1.z.string()),
});
/**
 * Where the issue lives, for a link out.
 *
 * ⚠️ **Both providers put the key in the path and neither needs an id**, so one helper serves both
 * — and a missing key answers `null` rather than a URL that 404s, because an issue link created by
 * `link-after-the-fact` carries an id and no key until the first sync reads it.
 */
function remoteIssueUrl(link) {
    if (link.remoteKey === null || link.remoteKey === '')
        return null;
    const base = link.baseUrl.replace(/\/+$/u, '');
    return link.extensionId === 'jira'
        ? `${base}/browse/${encodeURIComponent(link.remoteKey)}`
        : `${base}/issue/${encodeURIComponent(link.remoteKey)}`;
}
/**
 * Begin an OAuth connection.
 *
 * ⚠️ **`returnTo` is a PATH and the server re-checks it** (`oauth-state.ts`). It travels inside the
 * signed state and ends in a redirect, so a client that could put an absolute URL here would have
 * turned the callback into an open redirect we vouched for.
 */
exports.oauthStartRequestSchema = zod_1.z.object({
    returnTo: zod_1.z
        .string()
        .min(1)
        .max(300)
        .refine((value) => value.startsWith('/') && !value.startsWith('//'), {
        message: 'must be an in-app path',
    }),
});
/**
 * One tracker a person could connect their own account to.
 *
 * ⚠️ **`connected` is about THIS member and nobody else.** A screen that showed the workspace's
 * connections would tell somebody their hours are going out while they are quietly parking.
 */
exports.trackerConnectionSchema = zod_1.z.object({
    instanceId: zod_1.z.uuid(),
    name: zod_1.z.string(),
    extensionId: zod_1.z.enum(catalogue_1.EXTENSION_IDS),
    baseUrl: zod_1.z.string(),
    /** ⚠️ Null until discovery has run — a credential is keyed on the site, so there is nothing to key to. */
    remoteSiteId: zod_1.z.string().nullable(),
    connected: zod_1.z.boolean(),
    /**
     * 26 Oct 2026 · **whether this person is expected to connect** — their profile says they log time
     * (`tracks_time`). The *"your hours are not reaching Jira"* marks and prompt are for them only;
     * somebody who does not log can still connect, and is not nagged to.
     */
    required: zod_1.z.boolean(),
    expiresAt: zod_1.z.string().nullable(),
    scopes: zod_1.z.array(zod_1.z.string()),
});
/**
 * What the callback tells the screen, in the query string it redirects with.
 *
 * ⚠️⚠️ **A CODE, NEVER A SENTENCE** — rule 7 reaches a redirect too. The callback cannot know the
 * reader's language: it is reached by a browser Atlassian sent, carrying no session and therefore
 * no locale. So it hands back a key and the screen translates it.
 */
exports.CONNECT_OUTCOMES = [
    'ok',
    'cancelled',
    /** ⚠️ Atlassian refused us, which is NOT the same as the person declining. */
    'refused',
    'state',
    /** ⚠️ Ten minutes passed at the consent screen. *Try again* is the right advice here and only here. */
    'expired',
    'wrong_site',
    'no_refresh',
    'not_configured',
    'not_found',
    'malformed',
    'failed',
];
/**
 * 26 Oct 2026 · **the query the callback redirects with**, named once for the server that writes it
 * and the screens that read it and strip it.
 *
 * - `outcome` — one of `CONNECT_OUTCOMES`.
 * - `detail` — the PROVIDER's own words, untranslated, when there are any.
 * - `instance` — the instance the flow was for, only when the signed state verified: a rejected
 *   state names nothing we believe. What *Try again* starts over.
 * - `picked`, `expected` — `wrong_site` only: the site hosts the person's consent covered,
 *   comma-separated, and the host of the Jira this instance uses. Hosts, not names — Atlassian's
 *   site picker lists them by address.
 */
exports.CONNECT_RETURN_PARAMS = {
    outcome: 'connected',
    detail: 'detail',
    instance: 'instance',
    picked: 'picked',
    expected: 'expected',
};
//# sourceMappingURL=contract.js.map